import { useMemo } from 'react'
import {
  UnorderedList,
  ListItem,
  Text,
  SimpleGrid,
  Link,
} from '@chakra-ui/react'
import { useTranslation, Trans } from 'next-i18next'

import { logEvent } from '@/lib/gtm'
import env from '@/lib/env'
import { LEARN_PDPA } from '@/routes/external'

import Widget from '@/components/Containers/Widget'
import { TEXT_STYLE } from '@/features/landing/constants/FAQs'
import { Item as FAQItem } from '@/features/landing/components/FAQs'
import { listStyle } from '@/features/landing/components/Carousel/Contents/style'
import type { HeroProps } from '@/features/services/templates/Hero'
import type { ServiceInfoProps } from '@/features/services/templates/ServiceInfo'
import type { TextWithImageProps } from '@/features/services/templates/TextWithImage'
import { strongStyle } from '@/features/landing/components/Carousel/Contents/style'

import DesktopBgImage from '@/images/services/training/banner-desktop.jpg'
import MobileBgImage from '@/images/services/training/banner-mobile.jpg'
import InfoImage from '@/images/services/training/info.png'
import FloatImage from '@/images/services/training/floating.jpg'

import type { WidgetProps } from '../types'
import { TARGET_TEXT_STYLE } from './style'

export const useMeta = (): PageMeta => {
  const { t } = useTranslation('services')

  return {
    title: t('training.meta.title'),
    description: t('training.meta.description'),
    og: {
      title: t('training.meta.og.title'),
      description: t('training.meta.og.description'),
      image: {
        url: `${env.APP_URL}${t('training.meta.og.image.url')}`,
      },
    },
  }
}

export const useHeroData = (): HeroProps => {
  const {
    t,
    i18n: { language },
  } = useTranslation('services')

  return useMemo(
    () => ({
      title: t('training.title'),
      subtitle: t('training.subtitle'),
      image: {
        base: `url(${MobileBgImage.src})`,
        md: `url(${DesktopBgImage.src})`,
      },
      titleColor: 'primary.400',
      subtitleColor: 'gray.900',
      colorSchemePrimary: 'primary',
      colorSchemeSecondary: 'primary',
      bgColor: 'gray.25',
      onClickGetQuote: (): void => {
        logEvent({
          ga: {
            category: 'Training',
            action: 'Click get quote',
          },
        })
      },
      onClickGetHandout: (): void => {
        logEvent({
          ga: {
            category: 'Training',
            action: 'Click download handout',
            label: 'header',
          },
        })
      },
    }),
    [language],
  )
}

export const useServiceInfoData = (): ServiceInfoProps => {
  const {
    t,
    i18n: { language },
  } = useTranslation('services')

  return useMemo(
    () => ({
      title: t('training.serviceInfo.title'),
      paragraph: t('training.serviceInfo.paragraph'),
      image: InfoImage,
    }),
    [language],
  )
}

export const useCourseData = (): WidgetProps[] => {
  const {
    t,
    i18n: { language },
  } = useTranslation('services')

  return useMemo(
    () => [
      {
        image: '/images/services/training/courses-1.svg',
        text: (
          <Widget
            title={t('training.courses.contents.0.title')}
            sx={{ textAlign: 'center', py: 0 }}
          >
            <UnorderedList sx={listStyle}>
              <ListItem>{t('training.courses.contents.0.list.0')}</ListItem>
              <ListItem>{t('training.courses.contents.0.list.1')}</ListItem>
              <ListItem>{t('training.courses.contents.0.list.2')}</ListItem>
            </UnorderedList>
          </Widget>
        ),
        sx: {
          w: 'full',
        },
      },
      {
        image: '/images/services/training/courses-2.svg',
        text: (
          <Widget
            title={
              <Trans key="1" t={t} i18nKey="training.courses.contents.1.title">
                PDPA Awareness
                <br />
                training live &amp; webinar
              </Trans>
            }
            sx={{ textAlign: 'center', py: 0 }}
          >
            <UnorderedList sx={listStyle}>
              <ListItem>{t('training.courses.contents.1.list.0')}</ListItem>
              <ListItem>{t('training.courses.contents.1.list.1')}</ListItem>
              <ListItem>{t('training.courses.contents.1.list.2')}</ListItem>
            </UnorderedList>
          </Widget>
        ),
        sx: {
          w: 'full',
        },
      },
    ],
    [language],
  )
}

export const useTargetData = (): TextWithImageProps => {
  const {
    t,
    i18n: { language },
  } = useTranslation('services')

  return useMemo(
    () => ({
      title: t('training.benefit.title'),
      paragraph: (
        <UnorderedList sx={{ ...listStyle, ...TARGET_TEXT_STYLE }}>
          {new Array(5).fill(0).map((_, index) => {
            return (
              <ListItem key={`${index}`}>
                {t(`training.benefit.contents.${index}`)}
              </ListItem>
            )
          })}
        </UnorderedList>
      ),
      image: FloatImage,
      imageRatio: 452 / 402,
    }),
    [language],
  )
}

export const useFAQsData = (): FAQItem[] => {
  const {
    t,
    i18n: { language },
  } = useTranslation('faqs')

  return useMemo(
    () => [
      {
        title: t('FAQs.5.question'),
        content: <Text sx={TEXT_STYLE}>{t('FAQs.5.answer.paragraph')}</Text>,
      },
    ],
    [language],
  )
}

export const usePricingData = (): React.ReactNode => {
  const {
    t,
    i18n: { language },
  } = useTranslation('services')

  return useMemo(
    () => (
      <SimpleGrid
        templateColumns={{ base: '1fr', md: '1fr 1fr' }}
        gap={{ base: 6, sm: 7 }}
      >
        <Widget sx={{ textAlign: 'left' }}>
          <Text>
            <Trans t={t} i18nKey="training.pricing.contents.0">
              จัดอบรมที่บริษัท/นอกสถานที่:
              <Text as="strong" sx={{ ...strongStyle, display: 'block' }}>
                เริ่มต้นที่ 60,000 บาท
              </Text>
            </Trans>
          </Text>
        </Widget>
        <Widget sx={{ textAlign: 'left' }}>
          <Text>
            <Trans t={t} i18nKey="training.pricing.contents.1">
              จัดอบรมแบบ
              <Text as="strong" sx={{ ...strongStyle, display: 'block' }}>
                Live online: เริ่มต้นที่ 50,000 บาท
              </Text>
            </Trans>
          </Text>
        </Widget>
        <Widget
          sx={{ textAlign: 'left', gridColumn: { base: 1, md: '1 / 3' } }}
        >
          <Text>
            <Trans t={t} i18nKey="training.pricing.contents.2">
              เรียนด้วยตนเองผ่าน
              <Text as="strong" sx={{ ...strongStyle, display: 'block' }}>
                e-learning platform: เริ่มต้นที่ 600 บาท
              </Text>
              <Text
                as="span"
                sx={{ fontSize: { base: 'sm', sm: 'lg' }, display: 'block' }}
              >
                เลือกคอร์สเรียน PDPA ออนไลน์ด้วยตัวเองได้ที่
                <Link
                  isExternal
                  href={LEARN_PDPA.pathname}
                  sx={{
                    fontWeight: 'medium',
                    textDecoration: 'underline !important',
                    color: 'primary.400',
                  }}
                >
                  LearnPDPA
                </Link>
                (ไม่มีการปรับแต่งเนื้อหาให้เหมาะสมกับองค์กร)
              </Text>
            </Trans>
          </Text>
        </Widget>
      </SimpleGrid>
    ),
    [language],
  )
}

export const useWhyUsData = (): React.ReactNode[] => {
  const {
    t,
    i18n: { language },
  } = useTranslation('services')

  return useMemo(
    () => [
      <Trans key="1" t={t} i18nKey="training.whyUs.contents.0">
        อยากแน่ใจว่าพนักงานและผู้บริหารทุกคนเข้าใจถึงความรับผิดชอบของตนภายใต้
        <Text as="strong" sx={strongStyle}>
          PDPA
        </Text>
      </Trans>,
      <Trans key="2" t={t} i18nKey="training.whyUs.contents.1">
        ต้องการใช้เป็นหลักฐานการปฏิบัติตามกฎหมายเพื่อลดความเสี่ยง
      </Trans>,
      <Trans key="3" t={t} i18nKey="training.whyUs.contents.2">
        ต้องการ
        <Text as="strong" sx={strongStyle}>
          ลด Human error
        </Text>
        ในกระบวนการทำงานที่จะนำไปสู่การละเมิดข้อมูลส่วนบุคคล
      </Trans>,
    ],
    [language],
  )
}
